import {
	makeObservable,
	override,
	observable,
	action,
	reaction,
	runInAction,
} from 'mobx';
import {nanoid} from 'nanoid';
import type {
	SuccessResponse as UppySuccessResponse,
	Uppy,
	FileProgress,
} from '@uppy/core';
import {DetailedError} from 'tus-js-client';
import {dispatcher} from '@netx/core-next/instance/dispatcher';
import {
	normalizeStatusItemsOrIds,
	StatusItemStore,
	StatusItemStoreParams,
} from '@netx/core-next/store';
import * as onlineStatus from '@netx/core-components/hooks/useOnlineStatus';
import {UploadStatusItemStatus} from '../constants';
import {UploadWarning} from '../locales';
import type {UppyFileMeta, AnyUppyFile} from '../types';
import {getUppyFileObjectFromFile, getUppyFileIdFromFile} from '../utils';
import {UploadStatusItem} from './UploadStatusItem';

type UppyFileList<TMeta extends UppyFileMeta = UppyFileMeta> = Record<
	string,
	AnyUppyFile<TMeta>
>;

// type UppyCurrentUploads = Record<
// 	string,
// 	{
// 		fileIDs: string[];
// 		result: any; // TODO: what is this, the result of the upload batch?
// 		step: number;
// 	}
// >;

export interface UploadStatusStoreParams<
	D extends object = object,
	S extends string = UploadStatusItemStatus,
	TMeta extends UppyFileMeta = UppyFileMeta,
	T extends UploadStatusItem<D, S, TMeta> = UploadStatusItem<D, S, TMeta>,
> extends StatusItemStoreParams<D, S, T> {
	uppy: Uppy;
	isTokenUpload?: boolean;
}

// TODO: this really should be an abstract class as it cannot be executed on its own
// most of the logic can be - but not file add/what to do with the file after upload

export class UploadStatusItemStore<
	D extends object = object,
	S extends string = UploadStatusItemStatus,
	TMeta extends UppyFileMeta = UppyFileMeta,
	T extends UploadStatusItem<D, S, TMeta> = UploadStatusItem<D, S, TMeta>,
> extends StatusItemStore<D, S, T> {
	uppyUnsubscriber: () => void;
	uppy: Uppy;
	uppyIdsToItemIds = new Map<string, T>();
	offline = false;

	isTokenUpload = false;

	#uuid = `upload-store-${nanoid()}`;

	constructor({
		uppy,
		isTokenUpload,
		...opts
	}: UploadStatusStoreParams<D, S, TMeta>) {
		super(opts);

		this.offline = !navigator.onLine;
		this.isTokenUpload = isTokenUpload ?? false;

		makeObservable(this, {
			// inherited
			addItems: override,
			pauseAll: override,
			removeItems: override,
			resumeAll: override,

			// new
			addFiles: action,
			offline: observable,
		});

		this.uppy = uppy;
		this.handleFileAdded = this.handleFileAdded.bind(this);
		this.handleOnlineStatusChange = this.handleOnlineStatusChange.bind(this);
		this.handleRestored = this.handleRestored.bind(this);
		this.handleUploadError = this.handleUploadError.bind(this);
		this.handleUploadProgress = this.handleUploadProgress.bind(this);
		this.handleUploadRetry = this.handleUploadRetry.bind(this);
		this.handleUploadSuccess = this.handleUploadSuccess.bind(this);

		this.uppy
			.on('file-added', this.handleFileAdded as any)
			// This gets called whenever `file-added` gets called
			// not sure which would bring the most benefit atm - but it may be better to go through here
			// we will need an updateMany method though
			// .on('files-added', (files) => {
			// 	console.log('files-added', files);
			// })
			.on('restored', this.handleRestored)
			.on('upload-error', this.handleUploadError as any)
			.on('upload-progress', this.handleUploadProgress as any)
			.on('upload-retry', this.handleUploadRetry)
			.on('upload-success', this.handleUploadSuccess as any);

		// we need to tickle the session while uploads are active
		this.disposers.push(
			onlineStatus.on(this.handleOnlineStatusChange),
			() => this.uppy.off('file-added', this.handleFileAdded as any),
			() => this.uppy.off('restored', this.handleRestored),
			() => this.uppy.off('upload-error', this.handleUploadError as any),
			() => this.uppy.off('upload-progress', this.handleUploadProgress as any),
			() => this.uppy.off('upload-retry', this.handleUploadRetry),
			() => this.uppy.off('upload-success', this.handleUploadSuccess as any),
			// so... sometimes golden retiriever only gets state data
			// and no blobs etc... it releases no event so we must listen
			// this can actually come first and a `restored` gets fired later too
			// why we must allow for updating the status of an existing item in `addFiles`
			(this.uppyUnsubscriber = this.uppy.store.subscribe(
				(prevState, nextState, patch) => {
					if (patch.recoveredState) this.handleRestored();
				},
			)),
			reaction(
				() => this.hasActiveItems,
				(hasActiveItems) => {
					hasActiveItems
						? dispatcher.trigger('session:tickle:add', this.#uuid)
						: dispatcher.trigger('session:tickle:remove', this.#uuid);
				},
			),
			() => dispatcher.trigger('session:tickle:remove', this.#uuid),
		);
	}

	/** @override */
	addFiles(files: AnyUppyFile<TMeta> | AnyUppyFile<TMeta>[]): T[] {
		// must handle in extending classes
		// this is primarily due to how `data` is defined
		// TODO: maybe a prop on the class with a function that takes a file and returns data || null
		// also for imorting assets (and possibly other use cases) we want to split the progress
		// between upload and any additional tasks that take place after (and before the item is considered complete)
		// This could possibly be handled by the underlying item checking its current status
		return [];
	}

	// overriding to track uppy ids
	/** @override */
	addItems(items: T | T[], toBeginning = false) {
		items = super.addItems(items, toBeginning);
		items.forEach((item) => this.uppyIdsToItemIds.set(item.fileId, item));
		return items;
	}

	/**
	 * confirm restoration with uppy (golden retriever)\
	 * can only ever happen once
	 */
	confirmRestore() {
		const uppyState = this.uppy.getState<TMeta>();
		if (!uppyState.recoveredState) return false;

		const currentUploads = uppyState.currentUploads;

		Object.keys(currentUploads).forEach((uploadId) =>
			this.uppy.restore(uploadId),
		);

		this.uppy.setState({
			recoveredState: null,
		});

		return true;
	}

	/** get item by uppy file id */
	getByFileId(fileId: string) {
		return this.uppyIdsToItemIds.get(fileId);
	}

	/** an array of files to considered orphaned when restoring an upload */
	getOrphanedFiles(files: AnyUppyFile<TMeta>[]): AnyUppyFile<TMeta>[] {
		return files.filter((item) => item.meta.uniqueId == null);
	}

	/** an array of files to considered restorable when restoring an upload */
	getRestorableFiles(files: AnyUppyFile<TMeta>[]): AnyUppyFile<TMeta>[] {
		return files.filter(({meta}) => meta.uniqueId != null);
	}

	/** file was added to uppy */
	handleFileAdded(file: AnyUppyFile<TMeta>) {
		const item = this.getById(file.meta.uniqueId);
		if (!item?.missing) return;
		item.setStatus(UploadStatusItemStatus.RECOVERED as S);
	}

	/** an internet connection was lost or regained */
	handleOnlineStatusChange(isOnline: boolean) {
		runInAction(() => {
			this.offline = !isOnline;
		});
		if (isOnline) return;
		this.pauseAll();
	}

	/** files were restored via golden retriever */
	handleRestored() {
		this.uppyUnsubscriber();

		const files = this.uppy.getFiles<TMeta>();
		const filesToRestore = this.getRestorableFiles(files);

		// remove any files that were not yet submitted for uploading
		const filesToRemove = this.getOrphanedFiles(files);
		!!filesToRemove.length &&
			this.uppy.removeFiles(
				filesToRemove.map((file) => file.id),
				'cancel-all',
			);

		if (!filesToRestore.length) return;

		// make sure all recovered files are marked as paused
		this.uppy.setState({
			files: filesToRestore.reduce<UppyFileList<TMeta>>(
				(accum, file) => ({
					...accum,
					[file.id]: {
						...file,
						isPaused: true,
						isGhost: file.isGhost || file.data == null,
					},
				}),
				{},
			),
		});

		this.addFiles(filesToRestore);
	}

	/** uppy upload error */
	handleUploadError(file: AnyUppyFile<TMeta>, error: Error | DetailedError) {
		// sometimes uppy will fire this event and provide no file - good times
		const item = this.getById(file?.meta?.uniqueId);
		if (!item) return;

		let recoverable = false;
		// TODO: this is where we would set a recoverable error
		// check for details to offer in the status area
		if (
			!error.message.includes('response code: 403') &&
			('isNetworkError' in error || error instanceof DetailedError)
		) {
			// network error / tus error
			recoverable = true;
		} else {
			// who knows...
			this.uppy.removeFile(file.id);
		}

		item.setError(error.message, {recoverable});
	}

	/** uppy upload file progress */
	handleUploadProgress(
		file: AnyUppyFile<TMeta>,
		{bytesTotal, bytesUploaded}: FileProgress,
	) {
		const item = file && this.getById(file.meta.uniqueId);
		if (!item) return;
		item.setProgress(Math.round((bytesUploaded / bytesTotal) * 100));
		!item.paused && item.setStatus(UploadStatusItemStatus.UPLOADING as S);
	}

	/** uppy is retrying a file upload */
	handleUploadRetry(fileId: string) {
		this.getByFileId(fileId)?.resetError();
	}

	/** uppy successfully uploaded file */
	handleUploadSuccess(file: AnyUppyFile<TMeta>, response: UppySuccessResponse) {
		// sometimes there is no file...
		if (!file) return;

		const item = this.getByFileId(file.id);
		if (!item) return;

		// TODO: fix their typings
		const fileUri = (file as any).s3Multipart?.key || response.uploadURL;

		if (fileUri == null) {
			console.error(`fileUri could not be retrieved for ${file.id}`);
			return;
		}

		this.uploadResolved(item, fileUri);
	}

	// convey to uppy
	/** @override */
	pauseAll() {
		const itemsPaused = super.pauseAll();

		const updatedFiles = {...this.uppy.getState<TMeta>().files};
		const fileIds = Object.keys(updatedFiles).filter((fileId) => {
			const file = updatedFiles[fileId];
			return file.meta.uniqueId != null && !file.progress?.uploadComplete;
		});
		fileIds.forEach((fileId) => {
			const file = updatedFiles[fileId];
			if (!file) return;
			updatedFiles[fileId] = {...file, isPaused: true};
		});
		this.uppy.setState({files: updatedFiles});
		this.uppy.emit('pause-all');

		return itemsPaused;
	}

	// overriding to track uppy ids and dispatch uppy events
	/** @override */
	removeItems(itemsOrIds: T | T['id'] | Array<T | T['id']>) {
		const itemsRemoved = super.removeItems(itemsOrIds);

		const fileIds = itemsRemoved.map((item) => item.fileId);
		fileIds.forEach(this.uppyIdsToItemIds.delete.bind(this.uppyIdsToItemIds));

		try {
			this.uppy.removeFiles(fileIds, 'removed-by-user');
		} catch (ex) {}

		return itemsRemoved;
	}

	// TODO: `reselectFile` and `reselectFiles`
	// one is more specific to alerting the user that the file they "reselected"
	// is not missing/match any missing files
	// and the other is just to try and see what was replaced and report back
	// but these can easily be combined and the outside world using the store can decide
	// what to do with the returned info (an empty array would imply nothing replaced)
	/** reselect (missing) file */
	reselectFile(id: string, file: File) {
		const item = this.getById(id);
		if (!item) return false;

		const uppyFile = this.uppy.getFile(item.fileId);
		if (!uppyFile) {
			console.warn(`import reselect file: ${item.fileId} does not exist`);
			return false;
		}
		if (!uppyFile.isGhost && uppyFile.data != null) {
			console.warn(`import reselect file: ${item.fileId} is not missing`);
			return false;
		}

		const fileId = getUppyFileIdFromFile(file);

		if (fileId !== item.fileId) {
			dispatcher.trigger('app:snackbar', {
				message: UploadWarning.FILE_DOES_NOT_MATCH,
				severity: 'error',
			});
			return false;
		}

		this.uppy.addFile(getUppyFileObjectFromFile(file));
		item.setMissing(false);

		return true;
	}
	/** reselect (missing) files */
	reselectFiles(files: File[]) {
		const filesNotReplaced: File[] = [];
		files.forEach((file) => {
			const fileId = getUppyFileIdFromFile(file);
			const item = this.getByFileId(fileId);
			if (!item || !item.missing) {
				filesNotReplaced.push(file);
				return;
			}
			this.uppy.addFile(getUppyFileObjectFromFile(file));
			item.setMissing(false);
		});
		return filesNotReplaced;
	}

	// convey to uppy
	/** @override */
	resumeItems(itemsOrIds: T | T['id'] | Array<T | T['id']>) {
		const items = normalizeStatusItemsOrIds<D, S, T>(itemsOrIds).reduce<T[]>(
			(accum, itemOrId) => {
				const item =
					typeof itemOrId === 'string' ? this.getById(itemOrId) : itemOrId;
				return item ? [...accum, item] : accum;
			},
			[],
		);

		const fileIds = items.map((item) => item.fileId);
		// not yet recovered? - recover
		if (this.confirmRestore()) {
			// there is an issue in uppy's pause/resume stuff when confirm a restore
			// so - start upload, pause all items, then resume the ones that should be resumed
			this.uppy.upload();
			this.pauseAll();
		}

		const itemsResumed = super.resumeItems(items);
		const updatedFiles = {...this.uppy.getState<TMeta>().files};
		const uppyFileCount = Object.values(updatedFiles).length;
		fileIds.forEach((fileId) => {
			if (!updatedFiles[fileId]) return;
			updatedFiles[fileId] = {
				...updatedFiles[fileId],
				isPaused: false,
			};
		});
		this.uppy.setState({files: updatedFiles});

		uppyFileCount === fileIds.length
			? this.uppy.emit('resume-all')
			: fileIds.forEach((id) => this.uppy.emit('upload-pause', id, false));

		return itemsResumed;
	}

	/** retry uploading item */
	retry(id: string) {
		const item = this.getById(id);
		if (!item) return false;

		const uppyFile = this.uppy.getFile<TMeta>(item.fileId);
		if (!uppyFile) return false;

		item.resetError();
		item.resetProgress();
		item.setStatus(UploadStatusItemStatus.PENDING as S);

		this.uppy.retryUpload(item.fileId);

		return true;
	}

	/** toggle items paused state */
	toggleItemPause(id: string) {
		const item = this.getById(id);
		if (!item) return;

		const paused = super.toggleItemPause(id);

		this.uppy.setFileState(item.fileId, {
			isPaused: paused,
		});
		this.uppy.emit('upload-pause', item.fileId, paused);

		return paused;
	}

	/** uploaded file is ready for any further actions */
	async uploadResolved(item: T, fileUri: string): Promise<void> {
		// must handle in extending classes
	}
}

export default UploadStatusItemStore;
